import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { commentsAPI } from '../../services/api';
import LikeButton from './LikeButton';
import CommentForm from './CommentForm';
import AlertModal from '../common/AlertModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api/v1';

const CommentItem = ({ comment, onUpdate, onDelete, onReply, showReplyButton = true }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'error' });

  const handleEdit = async (content) => {
    try {
      const response = await commentsAPI.update(comment.id, content);
      onUpdate(response.data);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating comment:', error);
      setAlertModal({
        show: true,
        title: 'Error',
        message: 'Failed to update comment. Please try again.',
        type: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      await commentsAPI.delete(comment.id);
      onDelete(comment.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting comment:', error);
      setShowDeleteConfirm(false);
      setAlertModal({
        show: true,
        title: 'Error',
        message: 'Failed to delete comment. Please try again.',
        type: 'error'
      });
    }
  };

  const handleReplySubmit = async (content) => {
    try {
      const response = await commentsAPI.createReply(comment.id, content);
      onReply(response.data);
      setIsReplying(false);
    } catch (error) {
      console.error('Error posting reply:', error);
      setAlertModal({
        show: true,
        title: 'Error',
        message: 'Failed to post reply. Please try again.',
        type: 'error'
      });
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 604800)}w ago`;
    if (seconds < 31536000) return `${Math.floor(seconds / 2592000)}mo ago`;
    return `${Math.floor(seconds / 31536000)}y ago`;
  };

  return (
    <div className="space-y-3">
      <div className="bg-battle-gray-light rounded-lg p-4 border border-army/20 hover:border-army/40 transition-colors">
        <div className="flex space-x-3">
          {/* Avatar: uploaded > Google picture > initial placeholder */}
          <div className="flex-shrink-0">
            <Link to={`/user/${comment.user.id}`} className="block">
              {comment.user.has_avatar || comment.user.avatar_url ? (
                <img
                  src={comment.user.has_avatar
                    ? `${API_URL}/users/${comment.user.id}/avatar`
                    : comment.user.avatar_url}
                  alt={comment.user.display_name}
                  className="w-10 h-10 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="w-10 h-10 rounded-full bg-gradient-to-br from-army to-army-light flex items-center justify-center text-white font-bold text-sm"
                style={{ display: (comment.user.has_avatar || comment.user.avatar_url) ? 'none' : 'flex' }}
              >
                {comment.user.display_name.charAt(0)}
              </div>
            </Link>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center space-x-2 mb-1">
              <Link to={`/user/${comment.user.id}`} className="text-white font-semibold text-sm hover:text-army transition-colors">
                {comment.user.display_name}
              </Link>

              {comment.user.is_video_artist && (
                <span className="px-1.5 py-0.5 bg-army/20 text-army-light text-xs font-bold rounded border border-army/30">
                  ARTIST
                </span>
              )}

              <span className="text-gray-500 text-xs">
                {formatTimeAgo(comment.created_at)}
              </span>

              {comment.is_edited && (
                <span className="text-gray-600 text-xs">(edited)</span>
              )}
            </div>

            {/* Comment Text or Edit Form */}
            {isEditing ? (
              <CommentForm
                initialValue={comment.content}
                onSubmit={handleEdit}
                onCancel={() => setIsEditing(false)}
                submitButtonText="Save"
                isReply={true}
              />
            ) : (
              <>
                <p className="text-gray-300 whitespace-pre-wrap break-words mb-3 text-sm leading-relaxed">
                  {comment.content}
                </p>

                {/* Actions */}
                <div className="flex items-center space-x-4">
                  <LikeButton
                    type="comment"
                    id={comment.id}
                    initialLikesCount={comment.likes_count}
                    initialIsLiked={comment.is_liked}
                    isAuthenticated={!!user}
                  />

                  {showReplyButton && (
                    <button
                      onClick={() => setIsReplying(!isReplying)}
                      className="text-gray-400 hover:text-brand-yellow text-sm font-medium transition-colors"
                    >
                      Reply
                    </button>
                  )}

                  {comment.can_edit && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-gray-400 hover:text-brand-yellow text-sm font-medium transition-colors"
                    >
                      Edit
                    </button>
                  )}

                  {comment.can_delete && (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="text-gray-500 hover:text-red-400 text-sm font-medium transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>

                {/* Reply Form */}
                {isReplying && (
                  <div className="mt-4">
                    <CommentForm
                      onSubmit={handleReplySubmit}
                      onCancel={() => setIsReplying(false)}
                      placeholder={`Reply to ${comment.user.display_name}...`}
                      submitButtonText="Reply"
                      isReply={true}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-battle-gray rounded-xl max-w-md w-full p-6 border border-army/30 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4">Delete Comment?</h3>
            <p className="text-gray-400 mb-6">
              Are you sure you want to delete this comment? This action cannot be undone.
              {comment.replies_count > 0 && (
                /* battle-red kept: semantic danger warning about destructive action */
                <span className="block mt-2 text-battle-red">
                  This will also delete {comment.replies_count} {comment.replies_count === 1 ? 'reply' : 'replies'}.
                </span>
              )}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary text-sm !py-2 !px-4"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm font-bold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'error' })}
      />
    </div>
  );
};

export default CommentItem;
